import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Check } from "lucide-react";
import { FlatchMark, FlatchWordmark } from "@/components/FlatchLogo";

const AVATARS = [
    "https://images.unsplash.com/photo-1624610806209-82a4cbb4339a?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
    "https://images.unsplash.com/photo-1725033489648-a819750348eb?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
    "https://images.unsplash.com/photo-1497487231007-0a45e7b4b68a?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
];

const PERKS = [
    "60-second vibe quiz, matched by lifestyle",
    "Verified profiles + AI-moderated chat",
    "Free for everyone until Oct 9 — no card",
];

/**
 * Premium split-screen auth layout.
 * Left: branded marketing panel (desktop only). Right: form (children).
 */
export function AuthShell({ children }) {
    return (
        <div className="min-h-screen bg-background lg:grid lg:grid-cols-2">
            {/* Brand panel */}
            <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-primary text-primary-foreground p-12">
                <div className="pointer-events-none absolute inset-0 grain" />
                <div
                    className="pointer-events-none absolute -top-24 -left-16 h-96 w-96 rounded-full blur-3xl opacity-30"
                    style={{ background: "radial-gradient(closest-side, white, transparent)" }}
                />
                <div
                    className="pointer-events-none absolute bottom-0 right-0 h-80 w-80 rounded-full blur-3xl opacity-20"
                    style={{ background: "radial-gradient(closest-side, #d946ef, transparent)" }}
                />

                <div className="relative">
                    <Link to="/" className="inline-flex items-center gap-2" data-testid="auth-brand-home">
                        <FlatchMark size={40} tone="ghost" className="ring-1 ring-white/30" />
                        <span className="font-display text-xl font-black tracking-tight">Flatch</span>
                    </Link>
                </div>

                <div className="relative">
                    <h2 className="font-display font-black tracking-tighter text-5xl leading-[0.95]">
                        Find your <span className="font-fancy">flat.</span>
                        <br />
                        Meet your <span className="font-fancy">people.</span>
                    </h2>
                    <p className="mt-4 text-lg opacity-90 max-w-sm leading-relaxed">
                        The GenZ way to find flats and roommates you actually click with.
                    </p>
                    <ul className="mt-8 space-y-3">
                        {PERKS.map((p) => (
                            <li key={p} className="flex items-center gap-3 text-sm font-medium">
                                <span className="h-6 w-6 rounded-full bg-white/20 grid place-items-center flex-shrink-0">
                                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                                </span>
                                {p}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="relative flex items-center gap-3">
                    <div className="flex -space-x-2.5">
                        {AVATARS.map((a, i) => (
                            <img key={i} src={a} className="h-9 w-9 rounded-full object-cover ring-2 ring-primary" alt="" />
                        ))}
                    </div>
                    <div className="leading-tight">
                        <div className="flex text-white">
                            {[...Array(5)].map((_, i) => (
                                <Star key={i} className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} />
                            ))}
                        </div>
                        <div className="text-xs opacity-80 mt-0.5">loved by GenZ renters across India</div>
                    </div>
                </div>
            </div>

            {/* Form panel */}
            <div className="relative flex flex-col min-h-screen lg:min-h-0">
                <div
                    className="pointer-events-none absolute -top-32 right-0 h-72 w-72 rounded-full blur-3xl opacity-30 lg:hidden"
                    style={{ background: "radial-gradient(closest-side, hsl(270 90% 65% / 0.5), transparent)" }}
                />
                <div className="relative flex-1 flex items-center justify-center px-6 py-12">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="w-full max-w-md"
                    >
                        <Link to="/" className="inline-flex lg:hidden mb-8" data-testid="back-home-link">
                            <FlatchWordmark size={36} tone="coral" />
                        </Link>
                        {children}
                    </motion.div>
                </div>
            </div>
        </div>
    );
}

export default AuthShell;
